import { NgIf, UpperCasePipe } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatChip } from '@angular/material/chips';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { EDialogWidth } from '../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../core/validators/trimmed-required.validator';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../../shared/components/datatable/datatable.interface';
import { ErrorMessagesComponent } from '../../../../shared/components/error-messages/error-messages.component';
import { IDropdownOption, MatDropdown, TDropdownId } from '../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { StatusCategoryBadgeComponent } from '../../../../shared/components/status-category-badge/status-category-badge.component';
import { FormUtilitiesService } from '../../../../utilities/form-utilities.service';
import { HttpUtilitiesService } from '../../../../utilities/http-utilities.service';
import { BaseCrudPageComponent } from '../../../app/base-crud-page.component';
import { EWorkflow } from '../../../items/items.interface';
import { IStep, TAddStep, TFormControlStep } from './steps.interface';
import { IStepComponentState, StepsStore } from './steps.store';

@Component({
  imports: [
    DatatableComponent,
    ErrorMessagesComponent,
    MatButton,
    MatCard,
    MatCardContent,
    MatChip,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatDropdown,
    MatIcon,
    MatInput,
    MatLabel,
    NgIf,
    ReactiveFormsModule,
    StatusCategoryBadgeComponent,
    TranslateModule,
    UpperCasePipe,
  ],
  providers: [StepsStore, HttpUtilitiesService],
  selector: 'app-steps',
  standalone: true,
  templateUrl: './steps.component.html',
})
export class StepsComponent extends BaseCrudPageComponent<IStep, IStepComponentState> implements OnInit {
  @ViewChild('addEditFormDialog') addEditFormDialog!: TemplateRef<HTMLElement>;

  public readonly statusCategoryOptions: IDropdownOption<TDropdownId>[] = Object.values(EWorkflow).map(
    (category: EWorkflow): IDropdownOption<TDropdownId> => ({
      id: category,
      name: this.translate.instant(`system.label.${category}`),
    }),
  );

  public datatableConfigurations: IDatatableConfiguration<IStep> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.single,
    disableCheckboxCondition: (row: IStep): boolean => row.isDefault,
    editScope: ETableCrudScope.single,
  };

  public readonly datatableColumns: IDatatableColumn<IStep>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
  ];

  public readonly addEditForm: FormGroup<TFormControlStep> = new FormGroup({
    name: new FormControl('', [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)]),
    statusCategory: new FormControl<IDropdownOption<TDropdownId>[] | null>([], [Validators.required]),
  });

  constructor(
    public readonly store: StepsStore,
    public readonly formService: FormUtilitiesService,
  ) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfStep',
      multiContext: 'field.steps',
      nameProperty: 'name',
      singleContext: 'field.step',
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.datatableParams$.pipe(filter(Boolean)).subscribe((datatable) => {
        this.store.loadItems({
          fields: ['name', 'statusCategory', 'isDefault'],
          limit: datatable.limit,
          page: datatable.page,
          sort: [{ active: 'isDefault', direction: 'desc' }, datatable.sort],
          ...(datatable.search ? { search: { searchedFields: ['name'], searchText: datatable.search } } : {}),
        });
      }),
    );
  }

  public initializeAdd(): void {
    this.setupForAdd();
    this.addEditForm.enable();
    this.addEditForm.reset({ name: '', statusCategory: [] });

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public initializeEdit(): void {
    const [item] = this.selectedRows();

    if (!item || item.isDefault) {
      return;
    }

    this.setupForEdit();
    this.addEditForm.enable();
    this.addEditForm.reset({
      name: item.name,
      statusCategory: [{ id: item.statusCategory, name: this.translate.instant(`system.label.${item.statusCategory}`) }],
    });

    this.matDialog.open(this.addEditFormDialog, {
      disableClose: true,
      restoreFocus: false,
      width: EDialogWidth.xSmall,
    });
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      return;
    }

    const payload: TAddStep = this.formatOnePayload();

    if (!this.isEditDialog()) {
      this.store.createOne(payload);
    } else {
      const [item] = this.selectedRows();
      this.store.updateOne({ dto: payload, id: item.id });
    }

    this.matDialog.closeAll();
  }

  private formatOnePayload(): TAddStep {
    return {
      name: FormUtilitiesService.trimIfString(this.addEditForm.controls.name.value ?? ''),
      statusCategory: this.addEditForm.controls.statusCategory.value?.[0]?.id as EWorkflow,
    };
  }
}
